import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, generateReport, getReportsByGroup, Route, shouldRenderMobile } from '@philips-emr/hpms-provider-accreditation-commons';
import { ActionAPI, FetchAPI, FieldAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { SessionStorageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { TechnicalVisitDetailAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import './technical-visit.css';

import { getRenderInconsistency, Inconsistecy } from './technical-visit-inconsistencies-controller';

interface Form {
  ieStatus: number,
  dsStatus: string,
  nrSeqForm: number,
  nrSeqInconsistencyVisit: number,
  nrSeqAppointment: number,
  fillOutDate: string,
}

interface DateTypes {
  dateTimeStamp: string,
  fillOutDate: string,
}

const TECHNICAL_VISIT = '28';
const FINISHED = 4;

@Controller({ fragment: FragmentNames.TECHNICAL_VISIT_DETAIL })
export default class TechnicalVisitDetailsController extends BaseController {

  @Inject(Aliases.PANEL_LINK_INCONSISTENCIES)
  private linkInconsistencies: PanelAPI;
  @Inject(Aliases.PANEL_LINK_INCONSISTENCIES_MOBILE)
  private linkInconsistenciesMobile: PanelAPI;
  @Inject(Aliases.PANEL_LINK)
  private linkAppointments: PanelAPI;
  @Inject(Aliases.PANEL_LINK_MOBILE)
  private linkAppointmentsMobile: PanelAPI;
  @Inject(Aliases.ACTION_PRINT)
  private actionPrint: ActionAPI;

  @OnMount([Aliases.FETCH_VISIT_DATA, Aliases.FETCH_VISIT_FORM,
    Aliases.FETCH_HISTORIES, Aliases.FETCH_HISTORY_MOBILE,
    Aliases.FETCH_INCONSISTENCY, Aliases.FETCH_INCONSISTENCY_MOBILE])
  onMountDataFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqVisit });
    api.forceFetch();
  }

  @OnDynamicContentRender([Aliases.CARD_VISIT_FORM])
  onRenderStatusForm(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as unknown as Form;
    const stampForm = this.getStampForm(ieStatus, dsStatus);

    if (stampForm) {
      render((): ReactElement => stampForm);
    }
  }

  @OnMount([Aliases.FIELD_DATE, Aliases.FIELD_DATE_FORM])
  onMountFieldDate({ target }: MountEvent): void {
    const api = target as FieldAPI;
    const { dateTimeStamp, fillOutDate } = api.getData() as unknown as DateTypes;

    if (api.getAlias() === Aliases.FIELD_DATE) {
      api.setVisible(!!dateTimeStamp);
    } else {
      api.setVisible(!!fillOutDate);
    }
  }

  @OnDynamicContentRender([Aliases.CARD_HISTORIES, Aliases.CARD_HISTORY_MOBILE])
  onRenderAppointmentsHistory(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as unknown as Form;
    const stampForm = this.getStampAppointments(ieStatus, dsStatus);
    if (stampForm) {
      render((): ReactElement => stampForm);
    }

  }

  @OnDynamicContentRender([Aliases.DYNAMIC_CONTENT_FIELD_CARD_INCONSISTENCY, Aliases.CARD_INCONSISTENCY_MOBILE])
  onRenderContentInconsistency(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsSent, dsStatus } = data as Inconsistecy;
    const label = ieStatus == 3 ? dsSent : dsStatus;
    const element = getRenderInconsistency(ieStatus, label);
    if (element) {
      render((): ReactElement => element);
    }
  }

  @OnMount([Aliases.PANEL_DESKTOP])
  onMountPanelDesktop(event: MountEvent): void {
    const api = event.target as PanelAPI;
    api.setVisible(!shouldRenderMobile());
  }

  @OnMount([Aliases.PANEL_MOBILE])
  OnMountMobile(event: MountEvent): void {
    const api = event.target as PanelAPI;
    api.setVisible(shouldRenderMobile());
  }

  @OnClick([Aliases.LINK_APPOINTMENTS, Aliases.LINK_HISTORY_MOBILE])
  onClickLinkAppointmetns(): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.historyService.push(Route.TECHNICAL_VISIT_APPOINTMENTS, { nrSeqVisit });
  }

  @OnClick([Aliases.LINK_INCONSISTENCIES, Aliases.LINK_INCONSISTENCIES_MOBILE])
  onClickLinkInconsistencies(): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { nrSeqInconsistencyVisit } = this.context.getBulkData();
    if (nrSeqInconsistencyVisit) {
      SessionStorageService.setItem('nrSeqInconsistencyVisit', nrSeqInconsistencyVisit);
    }
    this.historyService.push(Route.TECHNICAL_VISIT_INCONSISTENCIES, { nrSeqVisit });
  }

  @OnSelectItem(Aliases.DECK_FORM)
  onClickForm(event: SelectItemEvent<any>): void {
    const { nrSeqForm } = event.item as Form;
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    this.historyService.push(Route.TECHNICAL_VISIT_FORMS, { nrSeqVisit, nrSeqForm });
  }

  @OnAfterFetch([Aliases.FETCH_INCONSISTENCY, Aliases.FETCH_INCONSISTENCY_MOBILE])
  onAfterFetchInconsistency({ data }: FetchEvent): void {
    if (isEmptyArrayOrObject(data)) {
      catchRenderException(() => {
        if (!shouldRenderMobile()) {
          this.linkInconsistencies.setVisible(false);
          return;
        }
        this.linkInconsistenciesMobile.setVisible(false);
      });
    } else {
      const inconsistency = data as Inconsistecy[];
      const { nrSeqInconsistencyVisit } = inconsistency[0];
      this.context.set({ nrSeqInconsistencyVisit });
    }
  }

  @OnAfterFetch([Aliases.FETCH_HISTORIES, Aliases.FETCH_HISTORY_MOBILE])
  onAfterFetchAppointments(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      catchRenderException(() => {
        if (!shouldRenderMobile()) {
          this.linkAppointments.setVisible(false);
          return;
        }
        this.linkAppointmentsMobile.setVisible(false);
      });
    }
  }

  @OnSelectItem([Aliases.DECK_INCONSISTENCY, Aliases.DECK_INCONSISTENCY_MOBILE])
  onSelectInconsistency(event: SelectItemEvent<any>): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { nrSeqInconsistencyVisit } = event.item as Form;
    SessionStorageService.setItem('nrSeqInconsistencyVisit', nrSeqInconsistencyVisit);
    this.historyService.push(Route.TECHNICAL_VISIT_INCONSISTENCIES, { nrSeqVisit });
  }

  @OnSelectItem([Aliases.DECK_HISTORIES, Aliases.DECK_HISTORY_MOBILE])
  onSelectHistory(event: SelectItemEvent<any>): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const { nrSeqAppointment } = event.item as Form;
    SessionStorageService.setItem('nrSeqAppointment', nrSeqAppointment);
    this.historyService.push(Route.TECHNICAL_VISIT_APPOINTMENTS, { nrSeqVisit });
  }

  @OnMount(Aliases.FIELD_CANCEL_REASON)
  onMountFieldCancelReason(event: MountEvent): void {
    const api = event.target as unknown as FieldAPI;
    if (api.getData()) {
      const { dsCancelReason } = api.getData();
      api.setVisible(!!dsCancelReason);
    }
  }

  private getStampForm(ieStatus: number, label: string): ReactElement | null {
    let stampProps = null;

    switch (ieStatus) {
      case 0:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
        break;
      case 1:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
        break;
      case 2:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label };
        break;
      case 3:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
        break;

      default:
        return null;
    }

    return <Stamp {...stampProps} />;
  }

  private getStampAppointments(ieStatus: number, label: string): ReactElement | null {
    let stampProps = null;

    switch (ieStatus) {
      case 1:
        stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
        break;
      case 2:
        stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
        break;
      case 3:
        stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
        break;
      case 4:
        stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE, label };
        break;
      case 5:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CLOCK, label };
        break;
      case 6:
        stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
        break;
      default:
        return null;
    }

    return <Stamp {...stampProps} />;
  }

  @OnMount(Aliases.ACTION_PRINT)
  async onMountActionPrint(): Promise<void> {
    this.actionPrint.setEnabled(false);
    const ieStatus = SessionStorageService.getItem('ieStatus') as number;
    if (ieStatus === FINISHED) {
      await getReportsByGroup(TECHNICAL_VISIT).then(resp => {
        if (resp.length > 0) {
          this.actionPrint.setEnabled(true);
        }
        return resp;
      });
    }
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClickActionPrint(): void {
    const { nrSeqVisit } = this.historyService.getCurrentRouteParams();
    const requestParams = {
      nrSeqVisit,
    };
    generateReport(Endpoint.POST.TECHNICAL_VISIT_REPORT, requestParams, TECHNICAL_VISIT)
      .finally(() => this.changeCursor(false));
  }

  private changeCursor(isProgress: boolean): void {
    document.body.style.cursor = isProgress ? 'wait' : 'default';
  }
}
